import React, { useState } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Check, Minus, Building2, UserPlus, Loader2, ExternalLink, MapPin } from 'lucide-react';

const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '';

function CheckColumn({ title, items }) {
  return (
    <div data-testid={`ii-col-${title.toLowerCase()}`}>
      <p className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground border-b border-border pb-1 mb-1.5">{title}</p>
      <ul className="space-y-0.5">
        {items.map((x, i) => (
          <li key={i} className={`text-[11px] inline-flex items-center gap-1.5 w-full ${x.checked ? 'text-foreground' : 'text-muted-foreground/50'}`}>
            {x.checked ? <Check className="w-3 h-3 text-emerald-400 shrink-0" /> : <Minus className="w-3 h-3 shrink-0" />}{x.label}
          </li>
        ))}
        {!items.length && <li className="text-[11px] text-muted-foreground/50">—</li>}
      </ul>
    </div>
  );
}

function FundsTable({ kind, funds }) {
  const rows = funds.filter(f => f.kind === kind);
  if (!rows.length) return null;
  return (
    <div data-testid={`ii-funds-${kind}`}>
      <p className="text-[11px] font-medium text-foreground mb-1">{kind === 'closed' ? 'Closed-end funds' : 'Open-end funds'} ({rows.length})</p>
      <table className="w-full text-[11px]">
        <thead><tr className="text-left text-muted-foreground border-b border-border">
          <th className="py-1 pr-2 font-medium">Fund</th><th className="py-1 pr-2 font-medium text-right">Deals</th><th className="py-1 pr-2 font-medium text-right">Size</th>
          <th className="py-1 pr-2 font-medium">{kind === 'closed' ? 'Vintage' : 'Inception'}</th><th className="py-1 pr-2 font-medium">Strategy</th><th className="py-1 pr-2 font-medium">Sector</th><th className="py-1 font-medium">Region</th>
        </tr></thead>
        <tbody className="divide-y divide-border/40">
          {rows.map((f, i) => (
            <tr key={i}><td className="py-1 pr-2 text-foreground">{f.name}</td><td className="py-1 pr-2 text-right font-mono">{f.deals_count || '—'}</td>
              <td className="py-1 pr-2 text-right font-mono">{f.size || '—'}</td><td className="py-1 pr-2 font-mono">{f.vintage || '—'}</td>
              <td className="py-1 pr-2">{f.strategy || '—'}</td><td className="py-1 pr-2">{f.sector || '—'}</td><td className="py-1">{f.region || '—'}</td></tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PeopleTable({ fundId, people, onLinked }) {
  const [busy, setBusy] = useState(null);
  const create = async (p, i) => {
    setBusy(i);
    try {
      const r = await api.iiPersonToContact(fundId, p);
      toast.success(`Kontakt „${r.data.name}“ angelegt (Typ Infra Investor)`);
      onLinked?.(i, r.data.id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Kontakt anlegen fehlgeschlagen'); }
    setBusy(null);
  };
  if (!people.length) return null;
  return (
    <div data-testid="ii-people-table">
      <p className="text-[11px] font-medium text-foreground mb-1">People ({people.length})</p>
      <table className="w-full text-[11px]">
        <thead><tr className="text-left text-muted-foreground border-b border-border">
          <th className="py-1 pr-2 font-medium">Name</th><th className="py-1 pr-2 font-medium">Title</th><th className="py-1 pr-2 font-medium">City</th><th className="py-1 pr-2 font-medium">Email</th><th className="py-1 pr-2 font-medium">Phone</th><th className="py-1" />
        </tr></thead>
        <tbody className="divide-y divide-border/40">
          {people.map((p, i) => (
            <tr key={i} data-testid={`ii-person-${i}`}>
              <td className="py-1 pr-2 text-foreground whitespace-nowrap">{p.name}</td><td className="py-1 pr-2">{p.title || '—'}</td><td className="py-1 pr-2 whitespace-nowrap">{p.city || '—'}</td>
              <td className="py-1 pr-2">{p.email ? <a href={`mailto:${p.email}`} className="text-sky-400/80 hover:text-sky-300">{p.email}</a> : '—'}</td>
              <td className="py-1 pr-2 font-mono">{p.phone || '—'}</td>
              <td className="py-1 text-right">
                {p.contact_id ? <a href={`/contacts?id=${p.contact_id}`} className="text-[10px] text-emerald-300 inline-flex items-center gap-1" data-testid={`ii-person-linked-${i}`}><Check className="w-3 h-3" /> im CRM</a> : (
                  <button onClick={() => create(p, i)} disabled={busy === i} data-testid={`ii-person-contact-btn-${i}`}
                    className="inline-flex items-center gap-1 text-[10px] rounded-sm px-2 h-6 border border-border text-foreground hover:border-amber-500/40 disabled:opacity-50">
                    {busy === i ? <Loader2 className="w-3 h-3 animate-spin" /> : <UserPlus className="w-3 h-3" />} Kontakt anlegen
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function OfficesTable({ offices }) {
  if (!offices.length) return null;
  return (
    <div data-testid="ii-offices-table">
      <p className="text-[11px] font-medium text-foreground mb-1">Offices ({offices.length})</p>
      <table className="w-full text-[11px]">
        <thead><tr className="text-left text-muted-foreground border-b border-border">
          <th className="py-1 pr-2 font-medium">Country</th><th className="py-1 pr-2 font-medium">City</th><th className="py-1 pr-2 font-medium">Address</th><th className="py-1 font-medium">Phone</th>
        </tr></thead>
        <tbody className="divide-y divide-border/40">
          {offices.map((o, i) => (
            <tr key={i}><td className="py-1 pr-2 text-foreground whitespace-nowrap">{o.country || '—'}</td><td className="py-1 pr-2 whitespace-nowrap">{o.city || '—'}</td><td className="py-1 pr-2">{o.address || '—'}</td><td className="py-1 font-mono whitespace-nowrap">{o.phone || '—'}</td></tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function IIProfileSection({ fund, onChanged }) {
  const ii = fund.ii;
  const [tab, setTab] = useState('funds');
  if (!ii) return null;
  const website = ii.website || fund.website;
  return (
    <div className="bg-card border border-border rounded-sm" data-testid="ii-profile">
      <div className="px-4 py-3 border-b border-border flex items-center justify-between gap-3 flex-wrap">
        <p className="text-[10px] uppercase tracking-widest font-bold text-sky-300/80 inline-flex items-center gap-1.5"><Building2 className="w-3 h-3" /> Infrastructure-Investor-Profil{ii.firm_type ? ` · ${ii.firm_type}` : ''}</p>
        <span className="text-[10px] text-muted-foreground/70" data-testid="ii-imported-at">{ii.imports} Import{ii.imports === 1 ? '' : 'e'} · {ii.screenshots} Screenshots · zuletzt {fmtDate(ii.imported_at)}{ii.imported_by_name ? ` von ${ii.imported_by_name}` : ''}</span>
      </div>
      <div className="p-4 space-y-4">
        <div className="grid sm:grid-cols-3 gap-2">
          {[['Headquarters', ii.headquarters, MapPin], ['Website', website, ExternalLink], ['AUM', ii.aum, null]].map(([k, v, Icon]) => (
            <div key={k} className="bg-secondary/60 border border-border rounded-sm px-3 py-2" data-testid={`ii-fact-${k.toLowerCase()}`}>
              <p className="text-[9px] uppercase tracking-widest text-muted-foreground">{k}</p>
              {k === 'Website' && v ? (
                <a href={v.startsWith('http') ? v : `https://${v}`} target="_blank" rel="noopener noreferrer" className="text-sm text-sky-400/90 hover:text-sky-300 inline-flex items-center gap-1">{v}{Icon && <Icon className="w-3 h-3" />}</a>
              ) : <p className="text-sm text-foreground">{v || '—'}</p>}
            </div>
          ))}
        </div>
        <div className="grid lg:grid-cols-[1.6fr_1fr_1fr_1fr] gap-4">
          <div>
            <p className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground border-b border-border pb-1 mb-1.5">About</p>
            <p className="text-[11px] text-muted-foreground leading-relaxed" data-testid="ii-about-text">{ii.about || '—'}</p>
          </div>
          <CheckColumn title="Regions" items={ii.regions || []} />
          <CheckColumn title="Strategies" items={ii.strategies || []} />
          <CheckColumn title="Sectors" items={ii.sectors || []} />
        </div>
        <div className="border-t border-border pt-3">
          <div className="flex items-center gap-1 mb-3">
            {[['funds', `Funds (${(ii.funds || []).length})`], ['contacts', `Contacts (${(ii.people || []).length} · ${(ii.offices || []).length} Offices)`]].map(([k, l]) => (
              <button key={k} onClick={() => setTab(k)} data-testid={`ii-tab-${k}`}
                className={`text-[11px] px-2.5 h-7 rounded-sm border ${tab === k ? 'border-amber-500/50 text-amber-200 bg-amber-500/10' : 'border-border text-muted-foreground hover:text-foreground'}`}>{l}</button>
            ))}
          </div>
          {tab === 'funds' ? (
            <div className="space-y-4">
              <FundsTable kind="closed" funds={ii.funds || []} /><FundsTable kind="open" funds={ii.funds || []} />
              {!(ii.funds || []).length && <p className="text-[11px] text-muted-foreground/60">Keine Funds importiert — Funds-Screenshot der II-Seite einfügen.</p>}
            </div>
          ) : (
            <div className="space-y-4">
              <PeopleTable fundId={fund.id} people={ii.people || []} onLinked={onChanged} />
              <OfficesTable offices={ii.offices || []} />
              {!(ii.people || []).length && !(ii.offices || []).length && <p className="text-[11px] text-muted-foreground/60">Keine Kontakte importiert — Contacts-Screenshot der II-Seite einfügen.</p>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
